// Dedicated Server contract from /backend/api/openapi/.
export type DedicatedServerPowerAction = 'start' | 'stop' | 'reboot' | 'restart'
export const dedicatedServerMetricsPeriods = ['1h', '24h', '14days', '30days', 'all'] as const
export type DedicatedServerMetricsPeriod = typeof dedicatedServerMetricsPeriods[number]
type DiskType = 'HDD' | 'SSD' | 'NVME' | 'MULTIPLE' | 'UNKNOWN'

type HardwareComponent = {
  model: string
  serial: string | null
  description: string | null
}

export type DedicatedServerHardware = {
  mainboard: HardwareComponent
  cpu: HardwareComponent & {
    count: number
    cores: number
    threads: number
    speed: number
    speed_turbo: number
    speed_type: string
  }
  ram: {
    value: number
    value_type: string
    count: number
    details: (HardwareComponent & { value: number; value_type: string })[]
  }
  disk: {
    disk_type: DiskType
    value: number
    value_type: string
    count: number
    details: {
      model: string
      serial: string | null
      disk_type: DiskType
      value: number
      value_type: string
    }[]
  }
}

export type DedicatedOperationProgress = {
  completed_steps?: number
  total_steps?: number
  steps_details?: { name?: string; display?: string; status?: string }[]
}

export type DedicatedServerOperation = {
  id: string
  name?: string
  extra_info: string
  operation_type: string
  primary_object_id?: string | null
  status?: string | null
  completed?: boolean
  created_at?: string
  progress: DedicatedOperationProgress | string | number | null
  initiating_user: { id?: number; email: string; full_name: string; display: string }
  seen: string
  can_retry: string
}

export type DedicatedServerListItem = {
  id: string
  name: string
  hostname: string
  os: string
  status: 'available' | 'active' | 'creating' | 'installing' | 'deleting'
  power_status: 'online' | 'offline' | 'unknown' | 'rebooting'
  reinstall_running: boolean
  hardware: DedicatedServerHardware
  tags: unknown
  last_seen_at: string | null
}

export type DedicatedServer = DedicatedServerListItem & {
  external_id: number
  operation: DedicatedServerOperation | null
  synced_at: string | null
}

export type DedicatedServerListResponse = {
  count: number
  next: string | null
  previous: string | null
  objects: DedicatedServerListItem[]
  totalCount: number
  pageNo: number
  operations: DedicatedServerOperation[]
}

export type DedicatedServerReinstall = {
  os: number
  password: string
  hostname?: string
  ssh_keys?: string[]
  disklayoutId?: number
}

export type DedicatedServerCreate = DedicatedServerReinstall & { server_id: string }
export type DedicatedServerUpdate = { hostname?: string; tags?: string[] }
export type DedicatedServerOperationResponse = { operation_id: string }

export type DedicatedServerPricePeriod = 'hour' | 'day' | 'month'
export type DedicatedServerPrice = {
  cpu: string
  ram: string
  disks: string
  total: string
  currency: string
}

export type DedicatedServerCreateOptions = {
  os: {
    id: number
    name: string
    allow_ssh_keys: boolean
    os_distro: string | null
  }[]
  servers: {
    id: string
    server_id: number
    name?: string
    state: string
    hardware: DedicatedServerHardware
    price: DedicatedServerPrice | null
  }[]
}

export type DedicatedServerNetworkMetric = {
  sensor_tag: string
  sensor_value: number
  status?: string | null
  time: string
  unit: string
}

export type DedicatedServerMetrics = {
  earliest_date: string
  name: string
  stats: DedicatedServerNetworkMetric[]
}
